import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { usePlan } from "@/context/PlanContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, getErrorMessage } from "@/lib/api";
import { loadConfig, calculateReport, getConfig, CATEGORIES } from "@/lib/calcEngine";
import { isoDate, formatDateDDMMYYYY as fmtDate } from "@/lib/format";
import { downloadReport, shareWhatsApp } from "@/lib/download";
import ResultDisplay from "@/components/ResultDisplay";
import CalcSwitcher from "@/components/CalcSwitcher";
import CustomEntry from "@/components/CustomEntry";
import { getActiveCalc } from "@/lib/customCalc";
import { LockedReport, PaywallSheet } from "@/components/Paywall";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { Minus, Plus, Calculator, Save, CheckCircle2, Palmtree, UtensilsCrossed, Wheat, FileDown, Sheet, Share2, Landmark, Banknote, X } from "lucide-react";

const CAT_COLOR = { balvatika: "#DB2777", primary: "#2563EB", upper_primary: "#059669", high_school: "#EA580C" };
const CAT_EMOJI = { balvatika: "🧒", primary: "🎒", upper_primary: "📘", high_school: "🎓" };
const DAY_EMOJI = ["☀️", "🌙", "🔥", "🌿", "⭐", "🌸", "🪔"];
const HOLIDAY_REASONS = ["Government Holiday", "Sunday", "Local Holiday", "School Closed", "Other"];

export default function Entry() {
  const { t, lang } = useLang();
  const { handle402, hasAccess, openUpgrade } = usePlan();
  const { user, refresh } = useAuth();
  const [sp] = useSearchParams();
  const [ready, setReady] = useState(false);
  const [date, setDate] = useState(sp.get("date") || isoDate(new Date()));
  const [counts, setCounts] = useState({ balvatika: 0, primary: 0, upper_primary: 0, high_school: 0 });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [dayType, setDayType] = useState("mdm");       // mdm | dry_ration | holiday
  const [payMode, setPayMode] = useState("dbt");
  const [holidayReason, setHolidayReason] = useState("Government Holiday");
  const [holidayOther, setHolidayOther] = useState("");
  const [editing, setEditing] = useState(false);
  const [savedSig, setSavedSig] = useState(null);   // signature of the last saved working-day entry
  const [savedFlash, setSavedFlash] = useState(false);
  const [busy, setBusy] = useState("");
  const [calcing, setCalcing] = useState(false);
  const [trial, setTrial] = useState(null);           // {enabled, limit, used, remaining, unlimited}
  const [dismissReminder, setDismissReminder] = useState(false);
  const [locked, setLocked] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [justUnlocked, setJustUnlocked] = useState(false);
  const pendingCalc = useRef(false);
  const [calcId, setCalcId] = useState(getActiveCalc());

  const logEvent = (event) => { api.post("/paywall/event", { event }).catch(() => {}); };

  // One canonical signature (Asia/Kolkata YYYY-MM-DD date) used to detect unsaved edits.
  const sigFrom = (dt, dType, pm, c) =>
    `${dt}|${dType}|${dType === "dry_ration" ? pm : ""}|${JSON.stringify(c)}`;
  const curSig = () => sigFrom(date, dayType, payMode, counts);
  const dirty = savedSig !== curSig();   // true when the on-screen data isn't the saved data

  useEffect(() => { if (user?.trial) setTrial(user.trial); }, [user]);

  const isFuture = useMemo(() => date > isoDate(new Date()), [date]);
  const dayInfo = useMemo(() => {
    if (!ready) return null;
    try { return calculateReport(date, { balvatika: 0, primary: 0, upper_primary: 0, high_school: 0 }); }
    catch { return null; }
  }, [date, ready]);

  const loadForDate = async (dstr) => {
    setResult(null); setErr(""); setEditing(false); setSavedSig(null);
    let loaded = false;
    try {
      const { data } = await api.get(`/entries/${dstr}`);
      const e = data.entry;
      const st = { balvatika: 0, primary: 0, upper_primary: 0, high_school: 0, ...e.students };
      setCounts(st);
      const dt = e.day_type || "mdm";
      setDayType(dt);
      const pm = e.payment_mode || "dbt";
      if (e.payment_mode) setPayMode(pm);
      if (e.holiday_reason) setHolidayReason(HOLIDAY_REASONS.includes(e.holiday_reason) ? e.holiday_reason : "Other");
      setEditing(true);
      setSavedSig(sigFrom(dstr, dt, pm, st));   // loaded entry is "clean"
      loaded = true;
    } catch {
      // smart default: Sunday -> Holiday, else MDM working day
      const wd = new Date(dstr + "T00:00:00").getDay();
      setDayType(wd === 0 ? "holiday" : "mdm");
      if (wd === 0) setHolidayReason("Sunday");
      try {
        const { data } = await api.get("/profile");
        if (data.profile?.enrolled) setCounts((p) => ({ ...p, ...data.profile.enrolled }));
        else setCounts({ balvatika: 0, primary: 0, upper_primary: 0, high_school: 0 });
      } catch { setCounts({ balvatika: 0, primary: 0, upper_primary: 0, high_school: 0 }); }
    }
    return loaded;
  };

  useEffect(() => {
    (async () => { await loadConfig(); await loadForDate(date); setReady(true); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDateChange = (v) => { setDate(v); loadForDate(v); };
  const total = CATEGORIES.reduce((a, c) => a + (parseInt(counts[c] || 0, 10) || 0), 0);
  const setC = (c, v) => { setCounts((p) => ({ ...p, [c]: Math.max(0, v) })); setResult(null); };

  const perStudent = (c) => {
    if (!dayInfo) return { cost: 0, rice: 0 };
    try {
      const r = calculateReport(date, { balvatika: 0, primary: 0, upper_primary: 0, high_school: 0, [c]: 1 });
      const cr = r.categories[c];
      return { cost: cr.per_student_total, rice: cr.rice_quantity_kg };
    } catch { return { cost: 0, rice: 0 }; }
  };

  const calculate = async () => {
    setErr("");
    if (total === 0) { setErr("Please enter at least one student."); return; }
    // Future dates: server preview (quantities/totals only), never counts against the free trial.
    if (isFuture) {
      try { const { data } = await api.post("/calc/preview", { date, students: counts }); setResult(data); }
      catch (e) { setErr(getErrorMessage(e) || "Unable to calculate for this date."); }
      return;
    }
    setCalcing(true);
    try {
      const { data } = await api.post("/calc/entry", { date, students: counts, day_type: dayType });
      setResult(data.result);
      setTrial((p) => ({ ...(p || {}), enabled: !data.unlimited || (p?.enabled ?? true),
        limit: data.limit, used: data.used, remaining: data.remaining, unlimited: data.unlimited }));
      setDismissReminder(false);
      // Unlocking after a successful payment (result arrives while a paywall was pending)
      if (pendingCalc.current) {
        pendingCalc.current = false;
        setLocked(false);
        setSheetOpen(false);
        setJustUnlocked(true);
        setTimeout(() => setJustUnlocked(false), 1200);
        logEvent("payment_success");
        const exp = user?.subscription?.expiry;
        const expFmt = exp ? new Date(exp).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";
        toast.success(`🎉 ${t("pw_unlocked").replace("{date}", expFmt)}`);
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        pendingCalc.current = true;
        const d = e.response?.data?.detail || {};
        setTrial((p) => ({ ...(p || {}), enabled: true, unlimited: false, remaining: 0,
          used: d.used ?? p?.used, limit: d.limit ?? p?.limit }));
        setResult(null);
        setLocked(true);
        setSheetOpen(true);
        logEvent("paywall_shown");
      } else {
        setErr(formatApiError(e.response?.data?.detail) || "Unable to calculate for this date.");
      }
    } finally { setCalcing(false); }
  };

  // After a successful payment the plan unlocks — auto-show the calculation the user just tried.
  useEffect(() => {
    if (hasAccess && pendingCalc.current) { pendingCalc.current = false; calculate(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess]);

  const doSave = async (payload, okMsg) => {
    if (isFuture) { toast.error(t("future_preview")); return false; }
    setSaving(true);
    try {
      const { data } = await api.post("/entries", { date, ...payload });
      setResult(data.entry.result);
      setEditing(true);
      const dt = payload.day_type || "mdm";
      setSavedSig(sigFrom(date, dt, payload.payment_mode || payMode, payload.students || counts));
      setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1800);
      toast.success(okMsg);
      return true;
    } catch (e) { if (!handle402(e)) toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setSaving(false); }
  };

  const save = async () => {
    if (!result) { calculate(); return; }
    await doSave({ students: counts, day_type: dayType, payment_mode: dayType === "dry_ration" ? payMode : undefined }, "Entry saved!");
  };

  const saveHoliday = async () => {
    const reason = holidayReason === "Other" ? (holidayOther || "Other") : holidayReason;
    await doSave({ students: { balvatika: 0, primary: 0, upper_primary: 0, high_school: 0 }, day_type: "holiday", holiday_reason: reason }, "Marked as holiday");
  };

  // Ensures the on-screen data is saved (auto-save if there are unsaved edits) before downloading.
  const ensureSaved = async () => {
    if (!dirty) return true;
    return doSave({ students: counts, day_type: dayType, payment_mode: dayType === "dry_ration" ? payMode : undefined }, "Entry updated ✓");
  };

  const download = async (fmt) => {
    if (!hasAccess) { openUpgrade(); return; }
    if (isFuture) { toast.error(t("future_preview")); return; }
    setBusy(fmt);
    try {
      if (!(await ensureSaved())) return;
      await downloadReport("daily", fmt, { d: date });
      toast.success(`${fmt === "excel" ? "Excel" : "PDF"} downloaded`);
    } catch (e) { if (!handle402(e)) toast.error(getErrorMessage(e)); }
    finally { setBusy(""); }
  };
  const share = async () => {
    if (isFuture) { toast.error(t("future_preview")); return; }
    setBusy("wa");
    try {
      if (!(await ensureSaved())) return;
      await shareWhatsApp("daily", { d: date }, `MDM entry ${fmtDate(date)}`);
    } catch (e) { if (!handle402(e)) toast.error(getErrorMessage(e)); }
    finally { setBusy(""); }
  };

  const switcher = <CalcSwitcher value={calcId} onChange={(c) => setCalcId(c?.id || "odisha")} />;
  if (calcId !== "odisha") {
    return (
      <div className="space-y-4 pb-36">
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold">{t("daily_entry")}</h1>
        {switcher}
        <CustomEntry key={calcId} calcId={calcId} initialDate={date} />
      </div>
    );
  }
  if (!ready) return <Loader />;
  const wd = dayInfo ? dayInfo.weekday_number : 0;
  const dayName = lang === "or" ? (dayInfo?.weekday_name_od || "") : (dayInfo?.weekday_name || "");
  const meal = dayInfo?.menu_display || "";
  const isEggDay = /egg|anda|ଅଣ୍ଡା/i.test(meal);

  return (
    <div className="space-y-5 pb-36">
      <h1 className="text-2xl md:text-3xl font-heading font-extrabold">{t("daily_entry")}</h1>
      {switcher}

      <div className="card-thali space-y-4">
        <div>
          <label className="font-semibold text-sm">{t("date")}</label>
          <input type="date" className="input-thali mt-1" value={date} onChange={(e) => onDateChange(e.target.value)} data-testid="entry-date" />
        </div>

        {/* Day card */}
        {dayInfo && (
          <div key={date} className="rounded-2xl p-4 bg-gradient-to-br from-thali-cream to-white border border-thali-turmeric/30 animate-fade-in-up" data-testid="entry-day-card">
            <div className="flex items-center justify-between">
              <div className="font-heading font-extrabold text-lg">{DAY_EMOJI[wd]} {dayName}</div>
              <div className="text-sm font-num text-thali-ink/60">{fmtDate(date)}</div>
            </div>
            <div className="text-sm mt-1 text-thali-ink/80" data-testid="entry-meal">🍽️ {t("todays_meal")}: {meal}</div>
            <div className="flex gap-2 mt-2">
              {dayInfo.laddoo_active && <span className="text-xs font-bold bg-thali-turmeric/30 rounded-full px-2 py-0.5" data-testid="badge-laddoo">🧁 {t("laddoo_day")}</span>}
              {isEggDay && <span className="text-xs font-bold bg-orange-100 text-orange-700 rounded-full px-2 py-0.5" data-testid="badge-egg">🥚 {t("egg_day")}</span>}
            </div>
          </div>
        )}
        {editing && <div className="text-xs font-bold text-thali-green bg-thali-green/10 rounded-full px-3 py-1 inline-block" data-testid="editing-chip">✏️ {t("editing_saved")}</div>}
        {isFuture && <div className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="future-note">⏳ {t("future_preview")}</div>}

        {/* Day type toggles */}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => { setDayType(dayType === "holiday" ? "mdm" : "mdm"); setResult(null); }}
            className={`min-h-[56px] rounded-xl font-bold flex items-center justify-center gap-2 border-2 transition-colors ${dayType !== "holiday" ? "bg-thali-green text-white border-thali-green" : "bg-white text-thali-ink/70 border-thali-turmeric/30"}`} data-testid="daytype-working">
            <CheckCircle2 className="w-5 h-5" /> ✅ {t("working_day")}
          </button>
          <button onClick={() => { setDayType("holiday"); setResult(null); }}
            className={`min-h-[56px] rounded-xl font-bold flex items-center justify-center gap-2 border-2 transition-colors ${dayType === "holiday" ? "bg-thali-ink text-white border-thali-ink" : "bg-white text-thali-ink/70 border-thali-turmeric/30"}`} data-testid="daytype-holiday">
            <Palmtree className="w-5 h-5" /> 🏖️ {t("holiday")}
          </button>
        </div>

        {dayType !== "holiday" && (
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => { setDayType("mdm"); setResult(null); }}
              className={`min-h-[52px] rounded-xl font-semibold flex items-center justify-center gap-2 border-2 ${dayType === "mdm" ? "bg-thali-saffron/15 border-thali-saffron text-thali-ink" : "bg-white border-thali-turmeric/30 text-thali-ink/60"}`} data-testid="worktype-mdm">
              <UtensilsCrossed className="w-4 h-4" /> 🍛 {t("mdm_serving")}
            </button>
            <button onClick={() => { setDayType("dry_ration"); setResult(null); }}
              className={`min-h-[52px] rounded-xl font-semibold flex items-center justify-center gap-2 border-2 ${dayType === "dry_ration" ? "bg-thali-turmeric/25 border-thali-turmericdark text-thali-ink" : "bg-white border-thali-turmeric/30 text-thali-ink/60"}`} data-testid="worktype-dry">
              <Wheat className="w-4 h-4" /> 🌾 {t("dry_ration_day")}
            </button>
          </div>
        )}

        {/* Holiday panel */}
        {dayType === "holiday" ? (
          <div className="rounded-2xl bg-thali-ink/5 border border-thali-ink/15 p-5 text-center space-y-3" data-testid="holiday-panel">
            <div className="text-4xl">🏖️</div>
            <div className="font-heading font-extrabold text-lg">{t("govt_holiday")} — {t("not_for_entry")}</div>
            <div className="text-sm text-thali-ink/70">ସରକାରୀ ଛୁଟି — ଏଣ୍ଟ୍ରି ପାଇଁ ନୁହେଁ</div>
            <div className="text-left">
              <label className="text-sm font-semibold">{t("holiday_reason")}</label>
              <select className="input-thali mt-1" value={holidayReason} onChange={(e) => setHolidayReason(e.target.value)} data-testid="holiday-reason">
                {HOLIDAY_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              {holidayReason === "Other" && <input className="input-thali mt-2" placeholder="Reason" value={holidayOther} onChange={(e) => setHolidayOther(e.target.value)} data-testid="holiday-other" />}
            </div>
            <button onClick={saveHoliday} disabled={saving || isFuture} className="btn-3d-green w-full" data-testid="mark-holiday-btn">
              <Save className="w-4 h-4" /> {saving ? "…" : t("mark_holiday")}
            </button>
          </div>
        ) : (
          <>
            {dayType === "dry_ration" && (
              <div className="rounded-xl bg-thali-turmeric/15 border border-thali-turmericdark/30 p-3 text-sm" data-testid="dry-banner">
                🌾 Dry Ration Day — no cooked meal today. Rice + cooking-cost amount is given to students.
                <div className="mt-2">
                  <div className="text-xs font-semibold mb-1">{t("payment_mode")}:</div>
                  <div className="flex gap-2">
                    <button onClick={() => { setPayMode("dbt"); setResult(null); }} className={`flex-1 min-h-[44px] rounded-lg text-sm font-semibold border-2 flex items-center justify-center gap-1 ${payMode === "dbt" ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/30"}`} data-testid="pay-dbt"><Landmark className="w-4 h-4" /> 🏦 {t("bank_dbt")}</button>
                    <button onClick={() => { setPayMode("cash"); setResult(null); }} className={`flex-1 min-h-[44px] rounded-lg text-sm font-semibold border-2 flex items-center justify-center gap-1 ${payMode === "cash" ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/30"}`} data-testid="pay-cash"><Banknote className="w-4 h-4" /> 💵 {t("cash_hand")}</button>
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="font-semibold text-sm">{dayType === "dry_ration" ? t("students_dry") : t("students")}</label>
                <span className="text-sm font-bold text-thali-green" data-testid="entry-total-present">{t("total_present")}: {total}</span>
              </div>
              <div className="space-y-2">
                {CATEGORIES.map((c) => {
                  const ps = dayType === "dry_ration" ? perStudent(c) : null;
                  return (
                    <div key={c} className="rounded-xl bg-white border border-thali-turmeric/30 p-2 pl-3" style={{ borderLeft: `5px solid ${CAT_COLOR[c]}` }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold flex items-center gap-2">{CAT_EMOJI[c]} {t(c)}</span>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setC(c, (parseInt(counts[c] || 0, 10) || 0) - 1)} className="w-11 h-11 rounded-lg bg-thali-cream font-bold text-xl" aria-label="decrease" data-testid={`entry-minus-${c}`}><Minus className="w-4 h-4 mx-auto" /></button>
                          <input value={counts[c]} inputMode="numeric" pattern="[0-9]*" onChange={(e) => setC(c, parseInt(e.target.value || 0, 10) || 0)} className="w-14 h-11 text-center font-num font-bold text-xl bg-transparent border-b-2 border-thali-turmeric/40" data-testid={`entry-input-${c}`} />
                          <button onClick={() => setC(c, (parseInt(counts[c] || 0, 10) || 0) + 1)} className="w-11 h-11 rounded-lg bg-thali-green text-white font-bold text-xl" aria-label="increase" data-testid={`entry-plus-${c}`}><Plus className="w-4 h-4 mx-auto" /></button>
                        </div>
                      </div>
                      {ps && <div className="text-xs text-thali-ink/60 mt-1" data-testid={`dry-perstudent-${c}`}>🌾 {!getConfig()?.redacted && <>Cost/student: ₹{ps.cost.toFixed(2)} · </>}Rice/student: {Math.round(ps.rice * 1000)} g</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {err && <div className="text-red-600 text-sm bg-red-50 rounded-lg p-2" data-testid="entry-error">{err}</div>}
            {trial && trial.enabled && !trial.unlimited && !hasAccess && trial.remaining != null && trial.remaining >= 1 && (
              <div className="flex justify-center" data-testid="free-calc-chip">
                {trial.remaining === 1 ? (
                  <span className="text-xs font-extrabold bg-red-100 text-red-700 rounded-full px-3 py-1 animate-fade-in-up">⚡ {t("last_free_calc")}</span>
                ) : trial.remaining === 2 ? (
                  <span className="text-xs font-bold bg-thali-saffron/20 text-thali-saffron rounded-full px-3 py-1">🎁 {trial.remaining} of {trial.limit} {t("free_calcs_left")}</span>
                ) : (
                  <span className="text-xs font-bold bg-thali-green/15 text-thali-green rounded-full px-3 py-1">🎁 {trial.remaining} of {trial.limit} {t("free_calcs_left")}</span>
                )}
              </div>
            )}
            <button onClick={calculate} disabled={calcing} className="btn-3d-saffron w-full text-lg" data-testid="entry-calculate"><Calculator className="w-5 h-5" /> {calcing ? "…" : t("calculate")}</button>
          </>
        )}
      </div>

      {locked && !result && dayType !== "holiday" && (
        <LockedReport date={date} counts={counts} menu={meal} />
      )}

      {result && dayType !== "holiday" && (
        <div className={justUnlocked ? "unblur-in space-y-5" : "space-y-5"}>
          {dayType === "dry_ration" && (
            <div className="card-thali bg-thali-turmeric/10" data-testid="dry-result-note">
              <div className="font-heading font-bold">🌾 Dry Ration — {payMode === "dbt" ? t("bank_dbt") : t("cash_hand")}</div>
              <div className="text-sm text-thali-ink/70 mt-1">Amount paid: <b>₹{result.summary.grand_total.toFixed(2)}</b> · Rice: <b>{result.summary.total_rice_kg} kg</b></div>
              <div className="text-xs text-thali-ink/50 mt-1">Cooking cost: ₹0.00 — Not applicable (paid as dry ration).</div>
            </div>
          )}
          <ResultDisplay result={result} animate={justUnlocked} />
          {trial && trial.enabled && !trial.unlimited && !hasAccess && trial.remaining != null && trial.remaining >= 1 && trial.remaining <= 2 && !dismissReminder && (
            <div className="card-thali bg-thali-saffron/10 border border-thali-saffron/30 relative" data-testid="trial-reminder-card">
              <button onClick={() => setDismissReminder(true)} className="absolute right-3 top-3 text-thali-ink/40" aria-label="Dismiss" data-testid="trial-reminder-close"><X className="w-4 h-4" /></button>
              <div className="text-sm font-semibold text-thali-ink pr-6">
                {t("trial_reminder_pre")} {trial.remaining} {t("trial_reminder_post")}
              </div>
              <button onClick={() => openUpgrade()} className="btn-3d-green w-full mt-3 text-sm" data-testid="trial-reminder-subscribe">{t("subscribe_now")}</button>
            </div>
          )}
          <button onClick={save} disabled={saving || isFuture} className="btn-3d-green w-full text-lg" data-testid="entry-save"><Save className="w-5 h-5" /> {saving ? "…" : t("save_entry")}</button>
        </div>
      )}

      {/* Sticky unlock bar (paywall closed but report still locked) */}
      {locked && !result && !sheetOpen && dayType !== "holiday" && (
        <div className="fixed left-0 right-0 bottom-[72px] md:bottom-4 z-30 px-4" data-testid="unlock-sticky-bar">
          <button onClick={() => { setSheetOpen(true); logEvent("paywall_shown"); }} className="btn-3d-saffron w-full max-w-3xl mx-auto flex shine-btn relative overflow-hidden" style={{ minHeight: 52 }}>
            🔓 Unlock ₹99
          </button>
        </div>
      )}

      <PaywallSheet open={sheetOpen} onClose={() => setSheetOpen(false)} logEvent={logEvent} />

      {/* Sticky download bar */}
      {(editing || result) && dayType !== "holiday" && !isFuture && (
        <div className="fixed left-0 right-0 bottom-[72px] md:bottom-4 z-30 px-4" data-testid="entry-download-bar">
          <div className={`max-w-3xl mx-auto bg-white/95 backdrop-blur rounded-2xl shadow-2xl border p-2 transition-all ${savedFlash ? "border-thali-green ring-2 ring-thali-green/40" : "border-thali-turmeric/30"}`}>
            {dirty && (
              <div className="text-[11px] font-semibold text-amber-700 bg-amber-50 rounded-lg px-2 py-1 mb-1.5 text-center" data-testid="entry-unsaved-hint">
                ⚠️ {t("unsaved_download_hint")}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => download("pdf")} disabled={busy || saving} className="btn-3d-saffron flex-1 text-sm" data-testid="dl-pdf"><FileDown className="w-4 h-4" /> {busy === "pdf" ? "…" : (dirty ? t("save_download_pdf") : t("download_pdf"))}</button>
              <button onClick={() => download("excel")} disabled={busy || saving} className="btn-3d-green flex-1 text-sm" data-testid="dl-excel"><Sheet className="w-4 h-4" /> {busy === "excel" ? "…" : (dirty ? t("save_download_excel") : t("download_excel"))}</button>
              {hasAccess && <button onClick={share} disabled={busy || saving} className="btn-ghost text-sm px-3" data-testid="dl-share"><Share2 className="w-4 h-4" /></button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
